import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { checkCoupon, listCoupons } from '@/api/coupons';
import {
  confirmFreeOrder,
  createPaymentOrder,
  getPaymentConfig,
  verifyPayment,
} from '@/api/payments';
import { LoginDialog } from '@/components/LoginDialog/LoginDialog';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useOrder } from '@/context/OrderContext';
import { useCartItems } from '@/hooks/useCartItems';
import { useRenewalItems } from '@/hooks/useRenewalItems';
import { useAddLanguageItems } from '@/hooks/useAddLanguageItems';
import { formatPrice } from '@/utils/format';
import { loadRazorpayScript, openRazorpayCheckout, toSubunits } from '@/utils/razorpay';
import { paths } from '@/routes/paths';
import { AddressSection } from './AddressSection';
import { DigitalAccessSection } from './DigitalAccessSection';
import { PaymentSection } from './PaymentSection';
import { OrderSummary } from './OrderSummary';
import { validateCheckout } from './validation';
import styles from './Checkout.module.css';

const EMPTY_ADDRESS = { name: '', phone: '', addr: '', city: '', pin: '' };

/**
 * The address as the backend models it (schemas/order.py). The form's own field
 * names grew out of the design prototype and don't match, so the mapping happens
 * here rather than renaming inputs the whole page reads.
 */
function toApiAddress(address) {
  return {
    name: address.name,
    line1: address.addr,
    city: address.city,
    pincode: address.pin,
    phone: address.phone,
  };
}

export function Checkout() {
  const navigate = useNavigate();
  const cart = useCart();
  const { saveOrder } = useOrder();
  const { user, isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();

  // Checkout settles exactly one currency. The cart drawer picks which one; with
  // nothing in the URL (a bookmark, a single-currency cart) useCartItems falls
  // back to the first group, so there is never a mixed total on this page.
  const requestedCurrency = searchParams.get('cur');
  // Renewing one book from the purchase history, rather than buying the cart.
  const renewBookId = searchParams.get('renew');
  const isRenewal = Boolean(renewBookId);
  // Adding languages to a book already owned, from its page. Like a renewal it
  // is one line for a copy the reader holds, never a cart line.
  const addLanguageBookId = searchParams.get('addlang');
  const isAddLanguage = !isRenewal && Boolean(addLanguageBookId);
  const requestedLanguages = useMemo(
    () => (searchParams.get('langs') ?? '').split(',').filter(Boolean),
    [searchParams],
  );
  // Either way the line is about a book already owned, not the cart.
  const isOwnedCopy = isRenewal || isAddLanguage;

  const [address, setAddress] = useState(EMPTY_ADDRESS);
  /**
   * At most one coupon, by construction: `applied` is a single offer, not a
   * list, so applying another replaces it. Codes are never combined — the
   * backend has nowhere to put a second one either.
   */
  const [coupon, setCoupon] = useState({
    input: '',
    applied: null,
    offers: [],
    checking: false,
    loading: false,
    error: '',
    maxDiscount: 0,
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState('');
  const [placed, setPlaced] = useState(false);
  /**
   * Why the sign-in dialog is open: 'pay' or 'coupon'. Both need a session —
   * an order is filed against a playTUB login, and coupon limits are counted per
   * customer — but they resume to different places once it closes.
   */
  const [signInIntent, setSignInIntent] = useState(null);
  const [gateway, setGateway] = useState(null);
  // A created order waiting on a gateway that doesn't exist — see handlePay.
  const [pendingMock, setPendingMock] = useState(null);
  // Set only after money has actually moved but the order could not be confirmed
  // here. Rendered instead of an error, because it is the opposite of one.
  const [settling, setSettling] = useState(null);

  // True from the moment payment starts until it is settled one way or another.
  // Checkout must not be navigated away from while any of it is outstanding.
  const inFlight = submitting || Boolean(pendingMock) || Boolean(settling);

  // The amount the server quoted for this exact cart. Shown, never decided here —
  // create-order reprices and revalidates before taking any money.
  const quotedDiscount = coupon.applied?.discount_amount ?? 0;
  const cartView = useCartItems({ currency: requestedCurrency, discount: quotedDiscount });
  // Both are always called — hooks cannot be conditional — and the URL picks one.
  const renewalView = useRenewalItems({
    bookId: renewBookId,
    addLanguages: requestedLanguages,
    discount: quotedDiscount,
  });
  const addLanguageView = useAddLanguageItems({
    bookId: isAddLanguage ? addLanguageBookId : null,
    languages: requestedLanguages,
  });
  const ownedView = isRenewal ? renewalView : addLanguageView;
  const { items, requiresDelivery, currency, isCatalogReady, isMultiCurrency, totals } =
    isOwnedCopy ? ownedView : cartView;

  // Which gateway, and whether it is real money. Only fetched once signed in —
  // the route needs a session — and a failure is not worth blocking checkout
  // over: it only decorates the payment panel, and create-order is the authority
  // on whether payment can actually proceed.
  useEffect(() => {
    if (!isSignedIn) return undefined;
    const controller = new AbortController();
    getPaymentConfig({ signal: controller.signal })
      .then(setGateway)
      .catch(() => {});
    return () => controller.abort();
  }, [isSignedIn]);

  // An empty cart has nothing to check out — but "empty" has three impostors,
  // and leaving this page mid-purchase loses the payment UI the shopper is in
  // the middle of. It is only really empty once the catalog has loaded (before
  // that every line looks unresolvable), once the account's own cart has been
  // read (signing in at checkout empties it for a round trip), and when we
  // haven't just emptied it ourselves by completing the order.
  const cartIsEmpty =
    !isOwnedCopy && items.length === 0 && isCatalogReady && cart.isLoaded && !placed && !inFlight;

  // A renewal that can't go ahead: the reader holds no purchase of this book, or
  // it isn't in its renewal window yet. Said plainly rather than redirecting, so
  // someone arriving from an old email learns why. Likewise a language that can't
  // be added: no purchase, nothing new chosen, or too little term left.
  const renewalUnavailable =
    isOwnedCopy &&
    isSignedIn &&
    ownedView.isReady &&
    !placed &&
    !inFlight &&
    (items.length === 0 ||
      (isRenewal ? !renewalView.entitlement?.renewable : !addLanguageView.entitlement?.addon_window_open));

  /**
   * The cart lines, as every pricing call wants them.
   *
   * Separate from buildPayload because the coupon endpoints need only the cart —
   * no address, no buyer — and they are called while the shopper is still
   * deciding, not at the point of paying.
   */
  const couponItems = useMemo(
    () =>
      items.map((i) => ({
        id: i.id,
        qty: i.qty,
        languages: i.languages,
        price: i.price,
        lineTotal: i.lineTotal,
        currency: i.currency,
      })),
    [items],
  );

  /** The coupons this shopper can use on this cart. Empty until they sign in —
   *  usage limits are counted per customer, so there is no list without one. */
  useEffect(() => {
    // No code comes off a language added to a book already owned, so there are
    // no offers to fetch for one.
    if (!isSignedIn || couponItems.length === 0 || isAddLanguage) {
      setCoupon((c) => ({ ...c, offers: [], loading: false }));
      return undefined;
    }

    const controller = new AbortController();
    setCoupon((c) => ({ ...c, loading: true }));
    listCoupons(couponItems, { signal: controller.signal })
      .then((res) =>
        setCoupon((c) => ({
          ...c,
          offers: res.coupons ?? [],
          maxDiscount: res.max_discount ?? 0,
          loading: false,
        })),
      )
      .catch(() => {
        // Not being able to offer a discount is a worse checkout, not a broken
        // one — a typed code still works.
        setCoupon((c) => ({ ...c, offers: [], loading: false }));
      });
    return () => controller.abort();
  }, [isSignedIn, couponItems, isAddLanguage]);

  /** Applies one coupon, replacing whatever was on the order before. */
  const applyCoupon = async (offer) => {
    setCoupon((c) => ({ ...c, applied: offer, input: '', error: '' }));
  };

  /**
   * Prices the typed code against this cart, and says why if it can't be used.
   *
   * Split from the button handler because the sign-in path calls it too, from a
   * callback that closed over a render where nobody was signed in — re-running
   * the gate there would just reopen the dialog it was resuming from.
   */
  const applyTypedCode = useCallback(async () => {
    const code = coupon.input.trim();
    if (!code) return;

    setCoupon((c) => ({ ...c, checking: true, error: '' }));
    try {
      const offer = await checkCoupon(couponItems, code);
      setCoupon((c) => ({ ...c, applied: offer, input: '', checking: false, error: '' }));
    } catch (err) {
      // The server's own reason — "that code has expired", "takes more off than
      // this order allows" — is more use than a generic failure.
      setCoupon((c) => ({
        ...c,
        checking: false,
        error: err.message ?? 'Could not check that code right now.',
      }));
    }
  }, [coupon.input, couponItems]);

  const handleApplyCode = () => {
    if (!coupon.input.trim()) return;

    // Coupon limits are counted per customer, so a code cannot be checked for
    // nobody. Asked for here rather than by disabling the field, so someone who
    // has a code can act on it instead of finding the box greyed out with no
    // way to sign in.
    if (!isSignedIn) {
      setCoupon((c) => ({ ...c, error: '' }));
      setSignInIntent('coupon');
      return;
    }
    return applyTypedCode();
  };

  const removeCoupon = () => setCoupon((c) => ({ ...c, applied: null, error: '' }));

  /** The cart as create-order and confirm-free both want it. */
  const buildPayload = useCallback(
    (buyer) => ({
      // A digital order has nowhere to be delivered, so no address is sent —
      // the backend rejects an order that needs one and doesn't have it.
      address: requiresDelivery ? toApiAddress(address) : null,
      // One code, or none. The shape is the rule.
      coupon_code: coupon.applied?.coupon_code ?? null,
      // Ignored by the real backend, which reprices the coupon itself. It
      // travels for the bundled mock, which has no coupon service to ask.
      coupon_discount: coupon.applied?.discount_amount ?? 0,
      // Who the confirmation is addressed to when there's no recipient on an
      // address. The real backend takes this from the session, not the body.
      buyerName: buyer?.display_name ?? buyer?.username ?? null,
      username: buyer?.username ?? null,
      items: items.map((i) => ({
        // The three fields the backend actually reads. Everything after them is
        // ignored there, and travels only so the bundled mock — which has no
        // catalogue to price from — can build the same receipt the API returns.
        id: i.id,
        qty: i.qty,
        languages: i.languages,
        kind: i.kind ?? 'purchase',
        name: i.name,
        price: i.price,
        basePrice: i.basePrice,
        languageCharges: i.languageCharges,
        lineTotal: i.lineTotal,
        language: i.language,
        format: i.format,
        currency: i.currency,
        tint: i.tint,
        deep: i.deep,
        letter: i.letter,
        image_url: i.image_url,
      })),
    }),
    [address, items, coupon.applied, requiresDelivery],
  );

  /** The order is real. Show it, and stop treating the cart as still holding it. */
  const finish = useCallback(
    (order) => {
      // Order the flag before clearing so the empty-cart redirect above stays quiet.
      setPlaced(true);
      saveOrder(order);
      // Only this currency's lines: books priced in another currency are still
      // waiting to be bought, and emptying the whole cart would lose them. A
      // renewal (or added language) was never in the cart, so there is nothing
      // to take out of it.
      if (!isOwnedCopy) cart.removeMany(items.map((i) => i.key));
      // The server dropped them from the stored cart as part of fulfilling the
      // order, so this reconciles against what was actually paid for instead of
      // trusting the guess above — and it settles any cart read still in flight
      // from signing in a moment ago.
      cart.refresh();
      navigate(paths.confirmation, { replace: true });
    },
    [cart, isOwnedCopy, items, navigate, saveOrder],
  );

  /**
   * Verifies a completed payment, and never tells a paying customer to try again.
   *
   * By the time this runs the money has moved. If the confirmation can't be
   * fetched — a dropped connection, a backend blip — retrying the *payment* would
   * charge them twice, so the failure is reported as what it is: paid, not yet
   * confirmed. Razorpay's webhook fulfils it server-side regardless, so the books
   * do arrive; this is only about what the shopper is told meanwhile.
   */
  const settlePayment = useCallback(
    async (payload) => {
      setStatus('Verifying your payment…');
      try {
        return finish(await verifyPayment(payload));
      } catch {
        // One retry: a verify that failed on the wire usually succeeds straight
        // after, and the alternative is a support ticket.
        try {
          return finish(await verifyPayment(payload));
        } catch (err) {
          setSettling({ paymentId: payload.razorpay_payment_id, detail: err.message });
          setSubmitting(false);
          setStatus('');
          // The server dropped these books from the stored cart as it fulfilled
          // the order, so re-read it rather than leaving the buyer looking at a
          // cart still holding what they have already paid for.
          cart.refresh();
          return undefined;
        }
      }
    },
    [cart, finish],
  );

  /**
   * Price it, charge it, confirm it.
   *
   * `buyer` is passed explicitly by the sign-in path: that call is made from the
   * dialog's onSuccess, which closes over the render where nobody was signed in
   * yet, so reading `user` from context there would still see null.
   */
  const handlePay = async (buyer = user) => {
    setError('');
    setSettling(null);
    setSubmitting(true);
    setStatus('Preparing your payment…');

    const payload = buildPayload(buyer);

    try {
      // 1. The server prices the cart and registers the amount with Razorpay.
      //    Nothing below can change that amount — only display it.
      const order = await createPaymentOrder(payload);

      // 2. Nothing to charge: free books, or a code that covered the lot.
      //    Razorpay cannot take a zero payment, so this skips it entirely.
      if (order.is_free || order.amount <= 0 || !order.order_id) {
        setStatus('Unlocking your books…');
        finish(await confirmFreeOrder(payload));
        return;
      }

      // 3. No keys configured, so there is no Checkout to open. Rather than
      //    pretend, hand the shopper the approval the gateway would have given.
      //    Only ever reachable in mock mode — the server refuses to mock when
      //    RAZORPAY_PAYMENT_MODE says it should be taking real money.
      if (order.is_mock || order.order_id.startsWith('order_mock_')) {
        setPendingMock({ order, payload });
        setStatus('');
        setSubmitting(false);
        return;
      }

      // 4. Razorpay's hosted Checkout. A blocked script is an error, not an
      //    invitation to simulate: on a live gateway "pretend it worked" would
      //    hand over books nobody paid for.
      setStatus('Opening Razorpay…');
      if (!(await loadRazorpayScript())) {
        setError(
          'We couldn’t load Razorpay’s secure checkout. Check your connection or any ad blocker, then try again.',
        );
        setSubmitting(false);
        setStatus('');
        return;
      }

      setStatus('Waiting for you to complete payment…');
      const result = await openRazorpayCheckout({
        key: order.razorpay_key_id,
        amount: toSubunits(order.amount),
        currency: order.currency,
        name: 'TUBMarket',
        description: order.description,
        order_id: order.order_id,
        // Sent explicitly rather than left blank. Razorpay Checkout remembers
        // returning customers on its own domain, so an empty field is filled
        // from whatever was last typed in that browser — which is how an
        // unrelated person's number ends up attached to this transaction.
        prefill: {
          name: order.prefill_name ?? undefined,
          email: order.prefill_email ?? undefined,
        },
        notes: { receipt: order.receipt ?? '' },
        theme: { color: '#e85d26' },
      });

      if (result.status === 'dismissed') {
        setError('Payment cancelled. Your cart is still here whenever you’re ready.');
        setSubmitting(false);
        setStatus('');
        return;
      }

      if (result.status === 'failed') {
        setError(paymentFailureMessage(result.error, gateway));
        setSubmitting(false);
        setStatus('');
        return;
      }

      // 5. Checkout says it was paid. That is not a purchase until the server
      //    has checked the signature, which is the only thing it cannot forge.
      await settlePayment({
        razorpay_order_id: result.response.razorpay_order_id,
        razorpay_payment_id: result.response.razorpay_payment_id,
        razorpay_signature: result.response.razorpay_signature,
      });
    } catch (err) {
      setError(err.message ?? 'Something went wrong taking your payment.');
      setSubmitting(false);
      setStatus('');
    }
  };

  /** Approves the simulated order in place of a Checkout modal that can't exist. */
  const approveMockPayment = async () => {
    if (!pendingMock) return;
    setSubmitting(true);
    setError('');
    // The mock signature the server accepts, and ONLY in mock mode — see
    // razorpay_service.verify_payment_signature. On a configured gateway this
    // is rejected like any other forgery.
    await settlePayment({
      razorpay_order_id: pendingMock.order.order_id,
      razorpay_payment_id: `pay_mock_${Date.now()}`,
      razorpay_signature: 'mock_sig_valid',
    });
    setPendingMock(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const problem = validateCheckout({ address, requiresDelivery });
    if (problem) {
      setError(problem);
      return;
    }

    // The purchase is registered against a playTUB login, so a valid session is
    // part of paying — not an optional convenience. Asked for here, after the
    // details check, so a signed-in-and-then-corrected form doesn't make anyone
    // authenticate twice.
    if (!isSignedIn) {
      setError('');
      setSignInIntent('pay');
      return;
    }

    await handlePay();
  };

  // Steps are numbered over the sections actually shown: a digital order has no
  // address to collect, so payment must not be labelled "2" there.
  const steps = requiresDelivery ? { address: 1, payment: 2 } : { access: 1, payment: 2 };

  if (cartIsEmpty) return <Navigate to={paths.home} replace />;

  if (isAddLanguage && (!isSignedIn || !addLanguageView.isReady || renewalUnavailable)) {
    const entry = addLanguageView.entitlement;
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Add a language</h1>
        <div className={styles.renewNotice}>
          {!isSignedIn ? (
            <>
              <p className={styles.renewNoticeText}>
                Sign in with the account you bought this book on to add a language to it.
              </p>
              <button type="button" className={styles.renewNoticeBtn} onClick={() => setSignInIntent('renew')}>
                Sign in
              </button>
            </>
          ) : !addLanguageView.isReady ? (
            <p className={styles.renewNoticeText}>Checking your purchase…</p>
          ) : (
            <>
              <p className={styles.renewNoticeText}>
                {!entry
                  ? 'This book isn’t one you own on this account.'
                  : entry.is_expired
                    ? 'This book has expired. Renew it, choosing the languages you want.'
                    : !entry.addon_window_open
                      ? 'This book’s term ends within a month. Renew it with the new language instead.'
                      : 'Choose a language you don’t have yet on the book’s page.'}
              </p>
              <Link to={paths.product(addLanguageBookId)} className={styles.renewNoticeBtn}>
                Back to the book
              </Link>
            </>
          )}
        </div>
        <LoginDialog
          open={signInIntent === 'renew'}
          reason="Languages are added to the account the book was bought on."
          onClose={() => setSignInIntent(null)}
          onSuccess={() => setSignInIntent(null)}
        />
      </div>
    );
  }

  if (isRenewal && (!isSignedIn || !renewalView.isReady || renewalUnavailable)) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Renew subscription</h1>
        <div className={styles.renewNotice}>
          {!isSignedIn ? (
            <>
              <p className={styles.renewNoticeText}>
                Sign in with the account you bought this book on to renew it.
              </p>
              <button type="button" className={styles.renewNoticeBtn} onClick={() => setSignInIntent('renew')}>
                Sign in
              </button>
            </>
          ) : !renewalView.isReady ? (
            <p className={styles.renewNoticeText}>Checking your purchase…</p>
          ) : (
            <>
              <p className={styles.renewNoticeText}>
                {renewalView.entitlement && renewalView.entitlement.renewal_opens_at
                  ? `This book can be renewed from ${formatDay(renewalView.entitlement.renewal_opens_at)}, a month before it expires.`
                  : 'This book isn’t one you can renew from this account.'}
              </p>
              <Link to={paths.orders} className={styles.renewNoticeBtn}>
                Back to your purchases
              </Link>
            </>
          )}
        </div>
        <LoginDialog
          open={signInIntent === 'renew'}
          reason="Renewals are tied to the account the book was bought on."
          onClose={() => setSignInIntent(null)}
          onSuccess={() => setSignInIntent(null)}
        />
      </div>
    );
  }

  return (
    <>
      <form className={styles.page} onSubmit={handleSubmit} noValidate>
        <h1 className={styles.title}>
          {isRenewal ? 'Renew subscription' : isAddLanguage ? 'Add a language' : 'Checkout'}
        </h1>
        <div className={styles.subtitle}>
          {isAddLanguage
            ? `For the rest of your copy's term — until ${formatDay(addLanguageView.entitlement?.expiry_date)}. Discount codes don't apply to languages added later.`
            : isRenewal
            ? renewalView.entitlement?.is_expired
              ? 'One more term of this book, starting the day you pay.'
              : `One more term of this book, starting when your current one ends on ${formatDay(renewalView.entitlement?.expiry_date)}.`
            : requiresDelivery
            ? 'Almost there — nice choices, by the way.'
            : 'Almost there — these open in playTUB the moment you pay.'}
          {isMultiCurrency && (
            <> You&apos;re paying for the {currency} books; the rest stay in your cart.</>
          )}
        </div>

        <div className={styles.layout}>
          <div className={styles.forms}>
            {requiresDelivery ? (
              <AddressSection
                step={steps.address}
                address={address}
                onChange={(field, value) => setAddress((a) => ({ ...a, [field]: value }))}
              />
            ) : (
              <DigitalAccessSection step={steps.access} />
            )}

            <PaymentSection step={steps.payment} config={gateway} currency={currency} />

            {pendingMock && (
              <div className={styles.mockPanel}>
                <div className={styles.mockTitle}>Approve simulated payment</div>
                <p className={styles.mockText}>
                  No Razorpay keys are configured, so there is no checkout to open. Approving here
                  completes the order exactly as a real payment would — without any money moving.
                </p>
                <div className={styles.mockActions}>
                  <button
                    type="button"
                    className={styles.mockApprove}
                    onClick={approveMockPayment}
                    disabled={submitting}
                  >
                    {submitting ? 'Confirming…' : `Approve ${formatPrice(totals.total, currency)}`}
                  </button>
                  <button
                    type="button"
                    className={styles.mockCancel}
                    onClick={() => setPendingMock(null)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {settling && (
              <div className={styles.settling} role="status">
                <strong>Your payment went through.</strong> We couldn&apos;t confirm the order from
                this device, but the payment is recorded and your books will appear in playTUB
                shortly. Please don&apos;t pay again.
                <span className={styles.settlingRef}>
                  Payment reference: {settling.paymentId} · check{' '}
                  <Link to={paths.orders}>your orders</Link> in a minute.
                </span>
              </div>
            )}
          </div>

          <OrderSummary
            items={items}
            totals={totals}
            currency={currency}
            requiresDelivery={requiresDelivery}
            coupon={coupon}
            allowCoupon={!isAddLanguage}
            isSignedIn={isSignedIn}
            onCouponInput={(value) => setCoupon((c) => ({ ...c, input: value }))}
            onApplyCouponCode={handleApplyCode}
            onSelectCoupon={applyCoupon}
            onRemoveCoupon={removeCoupon}
            error={error}
            submitting={submitting}
            status={status}
            payLabel={`Pay ${formatPrice(totals.total, currency)}`}
          />
        </div>
      </form>

      <LoginDialog
        open={signInIntent !== null}
        submitLabel={signInIntent === 'coupon' ? 'Sign in & apply' : 'Sign in & pay'}
        // A first-time buyer creating their account mid-checkout gets the same
        // button, still naming what happens after it.
        registerLabel={signInIntent === 'coupon' ? 'Create account & apply' : 'Create account & pay'}
        reason={
          signInIntent === 'coupon'
            ? 'Coupons are tied to your playTUB account, so we need to know which one.'
            : requiresDelivery
              ? 'Sign in so this order is filed against your playTUB account.'
              : 'Your books unlock on your playTUB account, so we need to know which one.'
        }
        onClose={() => setSignInIntent(null)}
        onSuccess={(signedInUser) => {
          const intent = signInIntent;
          setSignInIntent(null);
          // Resume whatever the sign-in interrupted. The coupon path needs no
          // user passed to it — the API reads the buyer from the session token,
          // which AuthContext has already installed by now.
          if (intent === 'coupon') applyTypedCode();
          else handlePay(signedInUser);
        }}
      />
    </>
  );
}

/** "20 October 2026" from an ISO timestamp. */
function formatDay(iso) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * What to say when Razorpay refuses a payment.
 *
 * Razorpay's own description is the useful part and is passed through. The one
 * addition is for the error that looks most like a bug in this app and isn't: an
 * Indian test account rejects every international card, which reads as "my card
 * doesn't work" to someone testing. Only ever added off a live gateway, where a
 * refusal is a real one and test-card advice would be nonsense.
 */
function paymentFailureMessage(error, gateway) {
  const description = error?.description || error?.reason || 'the payment could not be completed';
  const isInternationalCard = /international card/i.test(description);

  if (isInternationalCard && gateway && !gateway.is_live) {
    return `Payment failed: ${description}. This test account only accepts domestic Indian cards — try the test card shown above, or UPI "success@razorpay".`;
  }
  return `Payment failed: ${description}. You have not been charged.`;
}
